/**
 * LitSquad avatar transform data + helpers.
 *
 * The generated data files (`character-indices.generated.ts`,
 * `accessory-transforms.generated.ts`) are produced from the upstream
 * LitLegion Unity project — see `scripts/extract-litsquad-avatar-transforms.ts`.
 * The hand-written `coordinates.ts` module turns Unity world coordinates
 * into HTML canvas pixels.
 *
 * Public surface:
 *   - {@link lookupAccessoryTransform} — given an accessory identifier and
 *     a species, returns the per-character variant the avatar editor draws.
 *   - {@link getLitSquadCharacterIndex} — case-insensitive species → 0..16
 *     index lookup. Wraps the generated map so callers can pass either
 *     PascalCase (`'Bear'`) or lowercase (`'bear'`) — the metadata fields
 *     and inventory utilities use both casings.
 */

import {
  LITSQUAD_CHARACTER_INDEX,
  LITSQUAD_SPECIES_BY_INDEX,
  type LitSquadSpeciesName,
  getLitSquadCharacterIndex as getLitSquadCharacterIndexCaseSensitive,
} from './character-indices.generated';
import {
  DEFAULT_HANDS_TYPE,
  LITSQUAD_ACCESSORY_TRANSFORMS,
  type AccessoryTransform,
  type AccessoryTransformEntry,
  type AccessoryTransformsByCharacter,
} from './accessory-transforms.generated';

export {
  LITSQUAD_CHARACTER_INDEX,
  LITSQUAD_SPECIES_BY_INDEX,
  LITSQUAD_ACCESSORY_TRANSFORMS,
  DEFAULT_HANDS_TYPE,
};
export type {
  AccessoryTransform,
  AccessoryTransformEntry,
  AccessoryTransformsByCharacter,
  LitSquadSpeciesName,
};
export {
  PIXELS_PER_UNIT,
  ORIGIN_FRACTION,
  SPRITE_PPU,
  REFERENCE_CANVAS_SIZE,
  unityToCanvas,
  spriteCanvasSize,
  drawAccessoryWithTransform,
  getSourceImageSize,
} from './coordinates';

/**
 * Case-insensitive species → character index lookup. Inventory metadata
 * stores `character` lowercase (`'bear'`) while the type-level
 * `LitSquadSpecies` is PascalCase (`'Bear'`); both should resolve.
 */
export function getLitSquadCharacterIndex(species: string | null | undefined): number | null {
  if (!species) return null;
  // Try exact match first (PascalCase) — fastest path.
  const exact = getLitSquadCharacterIndexCaseSensitive(species);
  if (exact !== null) return exact;
  // Fall back to case-insensitive match against the canonical keys.
  const lower = species.toLowerCase();
  for (const key of Object.keys(LITSQUAD_CHARACTER_INDEX) as LitSquadSpeciesName[]) {
    if (key.toLowerCase() === lower) {
      return LITSQUAD_CHARACTER_INDEX[key];
    }
  }
  return null;
}

/**
 * Returns the transform variant the avatar editor should render for
 * a given accessory + species. Picks the variant whose `handsType`
 * matches `DEFAULT_HANDS_TYPE` (the standard standing pose), falling
 * back to the first variant in the bucket if there is no match.
 *
 * Returns `null` when the accessory is unknown, the species is
 * unknown, or the upstream Unity asset has no entry for that
 * character (rare — most accessories cover all 17 species).
 */
export function lookupAccessoryTransform(
  identifier: string | null | undefined,
  species: string | null | undefined,
): AccessoryTransform | null {
  if (!identifier) return null;
  const entry = LITSQUAD_ACCESSORY_TRANSFORMS[identifier];
  if (!entry) return null;

  const characterIndex = getLitSquadCharacterIndex(species);
  if (characterIndex === null) return null;

  const variants = entry.perCharacter[characterIndex];
  if (!variants || variants.length === 0) return null;

  const defaultVariant = variants.find((v) => v.handsType === DEFAULT_HANDS_TYPE);
  return defaultVariant ?? variants[0];
}

/**
 * Pulls the accessory identifier out of an inventory entry's metadata.
 * Crafted accessories carry `metadata.identifier` (set by the
 * crafting service), and legacy-imported accessories may carry
 * `Identifier` (PascalCase, from the legacy DFE stats payload).
 *
 * Returns `null` when the entry has no identifier — the caller should
 * skip transform application and fall back to a stretch-to-fill render.
 */
export function getAccessoryIdentifier(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  if (!metadata) return null;
  for (const key of Object.keys(metadata)) {
    if (key.toLowerCase() === 'identifier') {
      const value = metadata[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
  }
  return null;
}
